import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { classnames } from 'tailwindcss-classnames';
import Heading, { propsFromFields } from 'components/helpers/Heading';
import ArticlePageCard from 'components/helpers/Cards/article-page-card';
import EventPageCard from 'components/helpers/Cards/event-page-card';
import GenericPageCard from 'components/helpers/Cards/generic-page-card';
import CalloutcardCard, { CalloutCardItem } from 'components/helpers/Cards/calloutcard-card';
import { getEnumValue } from 'lib/util/enum';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination, Navigation } from 'swiper';
import Container from 'components/helpers/Container';
import EditingHelpText from 'components/helpers/EditingHelpText';
import { Core } from '.generated/templates/Foundation.Core.model';
import VideoCard from 'components/helpers/Cards/video-card';
import { VideoItemProps } from 'components/helpers/VideoItem/VideoItem';
import {
  pushCarouselClickDataLayerObject,
  OnLinkDataLayerPushProps,
  onCarouselCtaDataLayerPush,
} from 'lib/util/gtm-datalayer';
import { isArticlePage, isCalloutCard, isEventPage, isVideoItem } from 'lib/sitecore';

SwiperCore.use([Pagination, Navigation]);

export type CardCollectionProps = Sitecore.Override.ComponentBase &
  Components.General.CardCollection.Fields.CardCollection;

const CardCollection = ({ fields }: CardCollectionProps): JSX.Element => {
  const showCarousel = fields?.showAsCarousel.value;

  if (!fields) {
    return <></>;
  }

  // Temporarily disabling looping due to edge case bug
  const loopSwiper = true;

  const cardsToDisplay = parseInt(getEnumValue<string>(fields?.cardsToDisplay), 10);

  const isSingleCard: boolean = cardsToDisplay === 1 ? true : false;

  const cardWrapperConditionals = {
    'card-wrapper--1': cardsToDisplay === 1,
    'card-wrapper--2': cardsToDisplay === 2,
    'card-wrapper--3': cardsToDisplay === 3,
  };
  const cardColClass = classnames(cardWrapperConditionals);
  const gridColClass = classnames(
    'grid-flow-row',
    'sm:grid',
    cardWrapperConditionals,
    'mx-auto',
    {
      'lg:grid-cols-1': cardsToDisplay === 1,
      'lg:grid-cols-2': cardsToDisplay === 2,
      'lg:grid-cols-3': cardsToDisplay === 3,
    },
    'auto-rows-fr',
    'sm:gap-5'
  );

  const cardClasses = classnames(
    'flex',
    'h-auto',
    'flex-col',
    'mb-[16px]',
    'md:mb-[0px]',
    { 'lg:flex-row': cardsToDisplay === 1 },
    'items-center'
    // THIS CAN BE ADDED ONCE NEW SOLUTION HAS BE IMPLEMENTED
    // { 'md:items-stretch': cardsToDisplay === 1 }
  );

  const swiperControlsClasses = classnames(
    'm-auto',
    'my-8',
    'flex',
    'h-6',
    'w-max',
    'items-center',
    'lg:mb-0',
    // Applies hidden-class for swiper control buttons for md-size page and higher
    // if count of cards for displaying on swiper not less than count of cards in collection.
    { 'lg:hidden': cardsToDisplay >= fields?.cardList.length }
  );

  const gridCardClass = classnames(cardClasses);

  const cardsMapped = fields.cardList.map((value, index) => {
    let cardItem;

    const onCardCollectionGtmPush = (props: OnLinkDataLayerPushProps) => {
      onCarouselCtaDataLayerPush({
        pushEvent: props,
        index,
        carouselType: 'card collection',
        carouselText: value.displayName,
      });
    };

    const promotedPage = value as Sitecore.Override.ItemEx &
      Core.BaseTemplates.Routes.Fields.BasePage;

    if (!promotedPage) {
      return <></>;
    }

    if (isCalloutCard(value)) {
      cardItem = (
        <CalloutcardCard
          promotedPage={value as CalloutCardItem}
          isSingleCard={isSingleCard}
          onGtmPush={onCardCollectionGtmPush}
        />
      );
    } else if (isVideoItem(value)) {
      cardItem = <VideoCard promotedPage={value as VideoItemProps} isSingleCard={isSingleCard} />;
    } else if (isEventPage(value)) {
      cardItem = <EventPageCard promotedPage={promotedPage} isSingleCard={isSingleCard} />;
    } else if (isArticlePage(value)) {
      cardItem = <ArticlePageCard promotedPage={promotedPage} isSingleCard={isSingleCard} />;
    } else {
      cardItem = <GenericPageCard promotedPage={promotedPage} isSingleCard={isSingleCard} />;
    }

    return showCarousel ? (
      <SwiperSlide key={value.id} className={cardClasses}>
        {cardItem}
      </SwiperSlide>
    ) : (
      <li key={value.id} className={gridCardClass}>
        {cardItem}
      </li>
    );
  });

  let clickAction = '';
  // eslint-disable-next-line no-promise-executor-return, @typescript-eslint/no-explicit-any
  const handleRealIndexChange = async (swiper: any) => {
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((r) => setTimeout(r, 200));
    pushCarouselClickDataLayerObject(
      swiper.realIndex,
      clickAction,
      'card collection',
      fields.cardList
    );
    clickAction = '';
  };
  const handlePrev = () => {
    clickAction = 'carousel - left click';
  };
  const handleNext = () => {
    clickAction = 'carousel - right click';
  };

  const renderCardContainer = (list: {}) => {
    if (showCarousel) {
      return (
        <Swiper
          loop={loopSwiper}
          loopedSlides={cardsToDisplay}
          slidesPerView={1}
          slidesPerGroup={1}
          breakpoints={{
            1024: {
              slidesPerView: cardsToDisplay,
              slidesPerGroup: cardsToDisplay,
            },
          }}
          spaceBetween={30}
          navigation={{
            prevEl: '.swiper-button-prev',
            nextEl: '.swiper-button-next',
          }}
          pagination={{
            clickable: true,
            el: '.swiper-pagination',
            bulletElement: 'button',
          }}
          className={cardColClass}
          onRealIndexChange={handleRealIndexChange}
        >
          {list}
          <div slot="container-end" className={classnames('lg:mt-8')}>
            <div className={swiperControlsClasses}>
              <button type="button" className="swiper-button-prev" onClick={handlePrev}>
                prev
              </button>
              <div className="swiper-pagination" />
              <button type="button" className="swiper-button-next" onClick={handleNext}>
                next
              </button>
            </div>
          </div>
        </Swiper>
      );
    }
    return <ul className={gridColClass}>{list}</ul>;
  };

  const headingProps = propsFromFields(fields);

  return (
    <Container size="standard" dataComponent="authorable/general/cardcollection">
      <div className={fields?.description.value ? classnames('mb-8') : ''}>
        <Heading {...headingProps} />
      </div>
      {cardsMapped.length ? (
        <></>
      ) : (
        <EditingHelpText text="Card Collection | No Cards Selected in datasource." />
      )}
      {renderCardContainer(cardsMapped)}
    </Container>
  );
};

export default CardCollection;
